import { MutableRefObject, useCallback, useRef, useState } from 'react';
import { OnlyBotAnchor } from '@animavirtuality/onlybots-core';
import { useFrame } from '@react-three/fiber';
import { Euler, Group, MathUtils, Object3D, Vector3 } from 'three';
import { VoxelBotMaterial, BotInstancedMeshRef } from '@/lib/types';
import { DEFAULT_COLOR } from '@/lib/materials';
import { useEffectContext } from '@/hooks/useEffectContext';

// the maximum particle count to apply to rotation speed (serves as an upper limit)
const MAX_FACTOR = 32;

// then the base rotation speed
const ROTATION_SPEED = 0.009;

// scale of the overall paritcle cloud domain
const PARTICLE_CLOUD_SCALE = 0.4;
const PARTICLE_CLOUD_OFFSET = new Vector3(0, 0, 0);
const ROTATION = new Euler(0, 0, 0);
const TRANSFORMATION_STORAGE = new Object3D();
const POSITION_STORAGE = new Vector3();
const ROTATION_STORAGE = new Euler();
const CLOUD_COLOR = DEFAULT_COLOR;

type AnimationState = 'STATIC' | 'EXPLODING' | 'SWIRLING' | 'CONSOLIDATING';
const anchorPosition = new Vector3(0, 0, 0);

interface UseVoxelReturn {
    explode: () => void;
    consolidate: (materials: VoxelBotMaterial[], anchor: OnlyBotAnchor, onConsolidation?: () => void) => void;
    cycleBot: (materials: VoxelBotMaterial[], anchor: OnlyBotAnchor, onConsolidation: () => void) => void;
    animationState: AnimationState;
}

export const useVoxelExplosion = (
    refs: BotInstancedMeshRef[],
    m: VoxelBotMaterial[],
    groupRef: MutableRefObject<Group>,
    explodeTime: number = 0.5,
    swirlTime: number = 0.3,
    consolidateTime: number = 0.5,
    voxelCloudSpeed: number = ROTATION_SPEED,
    voxelCloudSize: number = PARTICLE_CLOUD_SCALE
): UseVoxelReturn => {
    const [materials, setMaterials] = useState<VoxelBotMaterial[]>(m);
    const [nextMaterials, setNextMaterials] = useState<VoxelBotMaterial[]>(m);
    const [nextAnchorPosition, setNextAnchorPosition] = useState<Vector3>(anchorPosition);
    const [animationState, setAnimationState] = useState<AnimationState>('STATIC');
    const [onConsolidationAction, setOnConsolidationAction] = useState<() => void>();
    const timer = useRef<number>(0);
    const consolidationTimer = useRef<number>(0);
    const animationProgress = useRef(0); // goes from 0->1 from static to swirling and 1 to 0 from swirling back to static
    const animationFactor = useRef<number>(0);
    const [readyToConsolidate, setReadyToConsolidate] = useState<boolean>(false);
    const { setEaseInBloom } = useEffectContext();

    const beginConsolidation = useCallback(() => {
        setMaterials(nextMaterials);
        setReadyToConsolidate(false);
        setAnimationState('CONSOLIDATING');
        if (onConsolidationAction) {
            onConsolidationAction();
        }
    }, [nextMaterials, onConsolidationAction]);

    useFrame((_, delta) => {
        if (animationState === 'STATIC') {
            timer.current = 0;
            return;
        }

        // update timer
        timer.current += delta;

        if (animationState === 'EXPLODING') {
            if (animationProgress.current !== 0 && timer.current > explodeTime) {
                animationProgress.current = 1;
                setAnimationState('SWIRLING');
            } else {
                animationProgress.current = MathUtils.clamp(timer.current / explodeTime, 0, 1);

                // turn materials from solid and colorful to semi-transparent and white
                refs.forEach((ref, i) => {
                    const refMaterial = ref?.current?.material;
                    const material = materials[i];

                    if (refMaterial && material) {
                        const { color } = material;

                        refMaterial.color.lerpColors(color, CLOUD_COLOR, animationProgress.current);
                    }
                });
            }
        } else if (animationState === 'SWIRLING') {
            if (timer.current > explodeTime + swirlTime && readyToConsolidate) {
                beginConsolidation();
                consolidationTimer.current = 0;
            }
        } else if (animationState === 'CONSOLIDATING') {
            consolidationTimer.current += delta;

            animationProgress.current =
                consolidationTimer.current > consolidateTime ? 0 : 1 - consolidationTimer.current / consolidateTime;

            refs.forEach((ref, i) => {
                const refMaterial = ref?.current?.material;
                const material = materials[i];

                if (refMaterial && material) {
                    const { color } = material;
                    refMaterial.color.lerpColors(color, CLOUD_COLOR, animationProgress.current);
                }
            });

            groupRef.current.position.lerp(nextAnchorPosition, animationProgress.current);
        } else {
            animationProgress.current = 0;
        }

        ROTATION.y -= voxelCloudSpeed * animationFactor.current;

        refs.forEach((ref, i) => {
            const mesh = ref?.current;

            if (!mesh) {
                return;
            }

            const material = materials[i];
            if (!material) {
                mesh.count = 0;
                return;
            }

            mesh.count = material.voxels.length;

            material.voxels.forEach(({ position: [posX, posY, posZ], particle }, index) => {
                let { factor } = particle;
                const { speed, xFactor, yFactor, zFactor } = particle;

                // hard limit factor, which speeds up voxels by count, to a max

                if (factor > MAX_FACTOR) {
                    factor = MAX_FACTOR;
                }

                animationFactor.current = factor / 4 + timer.current * (speed / 2);

                TRANSFORMATION_STORAGE.position.set(posX, posY, posZ);

                TRANSFORMATION_STORAGE.scale.setScalar(
                    MathUtils.lerp(1, Math.max(1.5, Math.cos(animationFactor.current) * 3), animationProgress.current)
                );

                // Augment rotation speed by distorted voxel position for more randomness

                ROTATION_STORAGE.copy(ROTATION);
                ROTATION_STORAGE.y *= (Math.cos(posY * 4.0) + 1.0) / 2.0;

                // update position for animation, particleAmount.current decreases as we animate,
                // eventually our position is the one defined by the bot

                POSITION_STORAGE.set(
                    xFactor +
                        Math.cos(animationFactor.current) +
                        Math.sin(animationFactor.current * 1) / 10 +
                        Math.cos((animationFactor.current / 10) * factor) +
                        (Math.sin(animationFactor.current * 1) * factor) / 10,

                    yFactor +
                        Math.sin(animationFactor.current) +
                        Math.cos(animationFactor.current * 2) / 10 +
                        Math.sin((animationFactor.current / 10) * factor) +
                        (Math.cos(animationFactor.current * 4) * factor * 4) / 10,

                    zFactor +
                        Math.sin(animationFactor.current) +
                        Math.cos(animationFactor.current * 2) / 10 +
                        Math.cos((animationFactor.current / 10) * factor) +
                        (Math.sin(animationFactor.current * 8) * (factor * 8)) / 10
                )
                    .applyEuler(ROTATION_STORAGE)
                    .multiplyScalar(voxelCloudSize)
                    .add(PARTICLE_CLOUD_OFFSET);

                TRANSFORMATION_STORAGE.position.lerp(POSITION_STORAGE, animationProgress.current);
                TRANSFORMATION_STORAGE.updateMatrix();

                mesh.setMatrixAt(index, TRANSFORMATION_STORAGE.matrix);
                mesh.instanceMatrix.needsUpdate = true;
            });
        });

        if (animationProgress.current === 0) {
            setAnimationState('STATIC');
        }
    });

    const explode = useCallback((): void => {
        setEaseInBloom(true);
        setAnimationState('EXPLODING');
    }, [setEaseInBloom]);
    const consolidate = useCallback(
        (materials: VoxelBotMaterial[], anchor: OnlyBotAnchor, onConsolidation?: () => void): void => {
            setNextMaterials(materials);
            setNextAnchorPosition(new Vector3(0, anchor.y, 0));
            setOnConsolidationAction(() => onConsolidation);
            setReadyToConsolidate(true);
        },
        []
    );
    const cycleBot = useCallback(
        (materials: VoxelBotMaterial[], anchor: OnlyBotAnchor, onConsolidation: () => void): void => {
            explode();
            consolidate(materials, anchor, onConsolidation);
        },
        [explode, consolidate]
    );
    return {
        explode,
        consolidate,
        cycleBot,
        animationState,
    };
};
